// @flow
// The way back from a broken screen: one tap reloads the app, without force-quitting it.
import { SUPPORT_WHATSAPP_URL } from './supportChat';

// The support team's WhatsApp, with a message saying why
export const SUPPORT_ERROR_WHATSAPP_URL = `${SUPPORT_WHATSAPP_URL}?text=Oi,%20o%20Doutore%20mostrou%20um%20erro%20agora`;

// What an error screen and Bugsnag say about anything thrown: an Error, a storage error object, null
export const errorMessage = (error: any): string => String((error && error.message) || error || 'unknown error');

// Reloads the app at its start (the home route): reloading the route that just broke could break again.
// Dropping the hash makes it a real page load, also in the native apps (cordova starts over).
export const reloadApp = () => {
  window.location.replace(window.location.href.split('#')[0]);
};

// The system browser in the native apps, a new tab on the web. In the apps the link itself would load
// WhatsApp's site in place of the app, with no way back: without the InAppBrowser plugin (deviceready
// never came) the tap does nothing (helpers/cordova.js openBillingPage does the same).
export const openSupportWhatsApp = (event: ?Object) => {
  if (!window.cordova) return;
  if (event && event.preventDefault) event.preventDefault();
  if (window.cordova.InAppBrowser) window.cordova.InAppBrowser.open(SUPPORT_ERROR_WHATSAPP_URL, '_system');
};

// A monotonic clock, in ms: the wall clock can jump (the system syncing its time, a laptop waking up)
export const hasMonotonicClock = (): boolean => Boolean(window.performance && typeof window.performance.now === 'function');
export const monotonicNow = (): number => (hasMonotonicClock() ? window.performance.now() : Date.now());

// Calls back once the page has been in front for `ms` in all. A hidden tab doesn't count: a browser restoring
// its tabs, a laptop waking up or a phone with the app in the background can hold a start for long with nobody
// looking. Returns a function that cancels it.
export const afterVisibleTime = (ms: number, callback: () => void) => {
  let left = ms;
  let since = 0;
  let timer = null;
  let done = false;
  const isHidden = () => document.visibilityState === 'hidden';

  const run = () => {
    if (done || timer || isHidden()) return;
    since = monotonicNow();
    timer = setTimeout(() => {
      timer = null;
      done = true;
      callback();
    }, Math.max(0, left));
  };
  const onVisibilityChange = () => {
    if (!isHidden()) {
      run();
    } else if (timer) {
      clearTimeout(timer);
      timer = null;
      left -= monotonicNow() - since;
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  run();
  return () => {
    done = true;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};

// How long the native apps wait, in front, for cordova's plugin scripts before starting over (they load in about 1 s)
export const PLUGINS_TIMEOUT = 8 * 1000;
// After a missing plugin module, before starting over: time for Bugsnag to send that error
export const MISSING_MODULE_RESTART_DELAY = 1500;
// A restart blocks another one for this long, until the start it made works. localStorage, not sessionStorage: the
// app's login already depends on it lasting across reloads, so a lost record can't turn restarts into a reload loop.
export const CORDOVA_RESTART_WINDOW = 2 * 60 * 1000;
const CORDOVA_RESTART_KEY = 'doutore-cordova-restart';

// What cordova's plugin loader throws when a plugin's script loaded without its module (it then never fires
// deviceready). index.html.ejs keeps it in window.cordovaPluginError when it comes before this bundle runs.
const MISSING_PLUGIN_MODULE = /Module \S+ does not exist\./;

type CordovaRestart = { reason: 'missing_module' | 'plugins_stuck', detail: string, at: number, used?: boolean };

const readRestart = (): ?CordovaRestart => {
  try {
    const saved = window.localStorage.getItem(CORDOVA_RESTART_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    return null;
  }
};

// Saved and read back: false when storage refuses or loses it
const saveRestart = (restart: CordovaRestart): boolean => {
  try {
    window.localStorage.setItem(CORDOVA_RESTART_KEY, JSON.stringify(restart));
    const saved = readRestart();
    return saved != null && saved.at === restart.at && saved.used === restart.used;
  } catch (error) {
    return false;
  }
};

const forgetRestart = () => {
  try {
    window.localStorage.removeItem(CORDOVA_RESTART_KEY);
  } catch (error) {
    // nothing saved, or nothing to save to
  }
};

// The restart that loaded this page, when it was one: for BlankPage, and for CordovaRestarted once deviceready comes
let thisPageRestart: ?CordovaRestart = null;
export const cordovaRestart = (): ?CordovaRestart => thisPageRestart;

// Whether cordova's plugin loader is done (null when cordova's channels can't be read), and what deviceready still
// waits for besides it
const cordovaChannels = (): ?{ pluginsLoaded: boolean, waiting: Array<string> } => {
  try {
    const channel = window.cordova.require('cordova/channel');
    return {
      pluginsLoaded: channel.onPluginsReady.state === 2,
      waiting: channel.deviceReadyChannelsArray.filter(c => c.state !== 2).map(c => c.type),
    };
  } catch (error) {
    return null;
  }
};

const startState = (channels: ?{ pluginsLoaded: boolean, waiting: Array<string> }): string => {
  if (!channels) return `cordova channels unknown, page ${document.readyState}`;
  return `plugins ${channels.pluginsLoaded ? 'loaded' : 'not loaded'}, waiting for ${channels.waiting.join(',') || 'nothing'}, `
    + `page ${document.readyState}`;
};

// The native apps start over once, on their own, when cordova's plugin loader fails. A plugin's script that came back
// empty (cordova throws "Module … does not exist.") or never finished loading leaves deviceready unfired and the page
// blank (cordova-ios 8.0.1's file serving on iOS 18, iPhone 4.16.49). Loading the page again is what the
// "Recarregar" of watchForBlankPage does, so it's done here without the 15 s of white and the tap.
// - A slow start whose plugins did load (the native side taking long on an old phone) is left alone.
// - Once per CORDOVA_RESTART_WINDOW unless the start it made worked: when it fails too, watchForBlankPage takes over
//   (after its own 15 s, so 8 + 15 s of white when the plugins get stuck twice).
// Calls onRestarted with the restart once the start it made gets to deviceready. Returns a function that stops watching.
export const retryFailedCordovaStart = (onRestarted: (restart: CordovaRestart) => void = () => {}) => {
  if (!window.cordova) return () => {};

  const saved = readRestart();
  const recent = saved != null && Math.abs(Date.now() - saved.at) < CORDOVA_RESTART_WINDOW;
  if (saved != null && recent && !saved.used) {
    // This page is that restart, if it can say so: later pages in the window (a "Recarregar") aren't
    if (saveRestart({ ...saved, used: true })) thisPageRestart = saved;
  } else if (saved != null && !recent) {
    forgetRestart();
  }

  let started = false;
  let restarting = false;
  let restartTimer = null;
  let stopTimer = () => {};

  const restart = (reason, detail, delay = 0) => {
    // Once: reloading cancels a stuck plugin's script, and cordova then throws "Module … does not exist." on the way out
    if (started || recent || restarting) return;
    if (!saveRestart({ reason, detail, at: Date.now() })) return;
    restarting = true;
    if (!delay) {
      reloadApp();
      return;
    }
    restartTimer = setTimeout(() => {
      if (!started) reloadApp();
    }, delay);
  };

  const onMissingModule = (message: string) => {
    restart('missing_module', `${message} (${startState(cordovaChannels())})`, MISSING_MODULE_RESTART_DELAY);
  };

  const onError = (event) => {
    const message = String((event && event.message) || '');
    if (MISSING_PLUGIN_MODULE.test(message)) onMissingModule(message);
  };

  const onPluginsLate = () => {
    const channels = cordovaChannels();
    if (channels && !channels.pluginsLoaded) restart('plugins_stuck', startState(channels));
  };

  const onDeviceReady = () => {
    started = true;
    clearTimeout(restartTimer);
    stopTimer();
    window.removeEventListener('error', onError);
    if (!thisPageRestart) return;
    // It worked: the next failure, even a minute from now, may start over again
    forgetRestart();
    onRestarted(thisPageRestart);
  };

  window.addEventListener('error', onError);
  stopTimer = afterVisibleTime(PLUGINS_TIMEOUT, onPluginsLate);
  document.addEventListener('deviceready', onDeviceReady, false);
  // Thrown before this bundle ran
  if (window.cordovaPluginError) onMissingModule(String(window.cordovaPluginError));

  return () => {
    clearTimeout(restartTimer);
    stopTimer();
    window.removeEventListener('error', onError);
    document.removeEventListener('deviceready', onDeviceReady, false);
  };
};

// How long a start may take before the page counts as blank (the app draws its spinner on deviceready)
export const BLANK_START_TIMEOUT = 15 * 1000;
// React drops the whole page when an error escapes every error boundary: give it a moment to draw again
export const BLANK_AFTER_EMPTY_DELAY = 1000;

const FALLBACK_ID = 'blankPageFallback';

const isEmpty = (root: HTMLElement) => !root.firstElementChild;

const removeFallback = () => {
  const fallback = document.getElementById(FALLBACK_ID);
  if (fallback) fallback.remove();
};

// Plain DOM, not React: React is what may be gone. Same look as the error screen (ErrorBoundaryView.scss).
const showFallback = () => {
  if (document.getElementById(FALLBACK_ID)) return;

  const screen = document.createElement('div');
  screen.id = FALLBACK_ID;
  screen.className = 'crashScreen';
  screen.setAttribute('role', 'alert');

  const title = document.createElement('h1');
  title.textContent = 'O Doutore não abriu direito';
  const text = document.createElement('p');
  text.className = 'crashScreen-text';
  text.textContent = 'Toque em Recarregar para abrir de novo. Se continuar, fale com o suporte.';
  const reload = document.createElement('button');
  reload.type = 'button';
  reload.className = 'crashScreen-reload';
  reload.textContent = 'Recarregar';
  reload.addEventListener('click', reloadApp);
  const support = document.createElement('a');
  support.className = 'crashScreen-support';
  support.href = SUPPORT_ERROR_WHATSAPP_URL;
  support.target = '_blank';
  support.rel = 'noopener noreferrer';
  support.textContent = 'Falar com o suporte';
  support.addEventListener('click', openSupportWhatsApp);

  screen.append(title, text, reload, support);
  (document.body: any).appendChild(screen);
};

type BlankReason = 'never_drew' | 'emptied';

// The last way out of a white page, whatever caused it: the app never drew (cordova's deviceready never
// came after the page was reloaded) or React dropped the whole page (an error past every boundary).
// Shows a "Recarregar" screen, and takes it away if the app draws after all. Returns a function that
// stops watching.
export const watchForBlankPage = (root: ?HTMLElement, onBlank: (reason: BlankReason) => void = () => {}) => {
  if (!root || !window.MutationObserver) return () => {};

  let drew = !isEmpty(root);
  let emptiedTimer = null;

  const blank = (reason: BlankReason) => {
    if (!isEmpty(root) || document.getElementById(FALLBACK_ID)) return;
    showFallback();
    onBlank(reason);
  };

  const startTimer = setTimeout(() => { if (!drew) blank('never_drew'); }, BLANK_START_TIMEOUT);

  const observer = new window.MutationObserver(() => {
    if (!isEmpty(root)) {
      drew = true;
      clearTimeout(emptiedTimer);
      removeFallback();
    } else if (drew) {
      clearTimeout(emptiedTimer);
      emptiedTimer = setTimeout(() => blank('emptied'), BLANK_AFTER_EMPTY_DELAY);
    }
  });
  observer.observe(root, { childList: true });

  return () => {
    observer.disconnect();
    clearTimeout(startTimer);
    clearTimeout(emptiedTimer);
  };
};
